
import '../../trackingRequest.css';
import _, { forEach } from 'lodash';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import { RequestType, TrackRequest } from '../../fms.core/TrackRequest';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { JobNames } from '../../framework.core/JobNames';
import { ApiResult } from '../../framework.core/data/ApiResult';
import SecondaryButton from '../../ui/SecondaryButton';
import ApplicationContext from '../../ApplicationContext';
import Fieldset from '../common/Fieldset';
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import LabeledControl from '../../ui/LabeledControl';
import Label from '../../ui/Label';
import LeftRightSplit from '../../ui/LeftRightSplit';
import Tracking_R from '../../img/Tracking_R.svg';
import { formatDateTime } from '../../dateUtils';
import JobListViewContext from '../common/JobListViewContext';
import constants from '../../constants';
import EditableTable, { CellData, TableCellTypes } from '../../ui/tables/editing/EditableTable';
import { ApplicableTableSettings, TableSettings } from '../../tableSettings';
import { getFmsGlobal } from '../../globals';
import PrimaryButton from '../../ui/PrimaryButton';
import { validateEmails } from '../../util';

const trackEmailTable:TableSettings = {
  tableVersion: 10012,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  flexResizer: true,

  alternateRows: true,
  columns: [
    { accessor:'email', Header: 'Emails', visible: true, resizable: false, sortable: false, filterable: false, width: 244 },
  ]
};

class TrackEmail {
  email: string = ''
}

interface TrackingRequestInitialResult {
  data: TrackRequest;
  email: TrackEmail[];
  groupTitle: string;
  groupVisible: boolean;
  groupItem: HTMLDivElement[];
  newRequest: boolean;
  visibleRequestBtn: boolean;
}

function processGroupData(
  groupData: TrackRequest[],
  jobName: JobNames,
  trType: string,
  mode: string
): TrackingRequestInitialResult | null {
  if (!groupData || groupData.length === 0 || !groupData[0]) return null;

  const trs = groupData;
  let visibleRequestBtn = trs[0].status !== 'tracking_stopped';
  let newRequest = false;

  if (trs[0].id <= 0) {
    // NoticeTo 는 백엔드(GetTrackRequest)에서 같은 customer 의 직전 NoticeTo 를 자동채움해주므로 보존
    trs[0].requestKind = trType === 'M' ? RequestType.bl : RequestType.Container;
    newRequest = true;
  }

  if (trs[0].id > 0 && trs[0].status === 'failed') {
    if (trs[0].statusDesc === 'not_found' || trs[0].statusDesc === 'awaiting_manifest' ||
      trs[0].statusDesc.indexOf('is not a valid') > -1) {
      newRequest = true;
      visibleRequestBtn = true;
    } else if (trs[0].statusDesc === 'retries_exhausted') {
      const trDate = new Date(trs[0].u2Date);
      const eta = new Date(trs[0].eta);
      if (trDate && eta && trDate < eta) {
        newRequest = true;
        visibleRequestBtn = true;
      } else {
        visibleRequestBtn = false;
      }
    } else {
      visibleRequestBtn = false;
    }
  }

  const groupTitle = trs[0].requestKind === RequestType.bl ? 'By MBL' : 'By Container';
  trs.sort((a, b) => a.cntrNo.localeCompare(b.cntrNo));

  const groupItem: HTMLDivElement[] = [];
  trs.forEach(tr => {
    const divElement = document.createElement('div');
    const text = _.isEmpty(tr.cntrNo) ? 'NO CONTAINER#' : tr.cntrNo;
    divElement.innerHTML = (tr.status === 'failed' ? `<img src="${Tracking_R}"/> ` : '') + text;
    divElement.className = 'trackingrequest-item';
    divElement.dataset.tooltip = tr.status === 'failed' ? tr.statusDesc : '';
    groupItem.push(divElement);
  });

  const groupVisible = !(trs.length === 1 && jobName === JobNames.OIContainer);

  const email: TrackEmail[] = [];
  if (trs[0].noticeTo) {
    trs[0].noticeTo.split(';').forEach(x => email.push({ email: x }));
  }

  return {
    data: trs[0],
    email,
    groupTitle,
    groupVisible,
    groupItem,
    newRequest,
    visibleRequestBtn,
  };
}

function emailRenderer({ data, row }: any) {
  const applyToLineItem = data[0].applyToLineItem;

  return (
    <TextBox
      id={`tracking-email-${row.index}`}
      value={data[row.index].email}
      maxLength={60}
      onChange={e => {
        applyToLineItem(row.index, { email: e.target.value })
      }}
    />
  );
}

function TrackingRequestImpl({
  jobId,
  storageKey,
  onClose,
  jobName,
  trStatus,
  trType,
  mode,
  vcResult,
  containerNo,
  sendTo,
  reloadData,
  prefetchedGroupData,
}: {
  jobId: number,
  storageKey: string,
  onClose: VoidFunction,
  jobName: JobNames,
  trStatus: string,
  trType: string,
  mode: string,
  vcResult: string,
  containerNo: string
  sendTo?:string,
  reloadData?: (trakingDate: string) => void,
  prefetchedGroupData?: TrackRequest[],
}) {
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);

  const initialResult = useMemo(() => {
    if (!prefetchedGroupData) return null;
    return processGroupData(prefetchedGroupData, jobName, trType, mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [initialized, setInitialized] = useState(!!initialResult);
  const [newRequest, setNewRequest] = useState(initialResult?.newRequest ?? false);

  const groupTitleRef = useRef(initialResult?.groupTitle ?? '');
  const groupVisibleRef = useRef(initialResult?.groupVisible ?? true);

  const groupItem = useRef<HTMLDivElement[]>(initialResult?.groupItem ?? []);

  const [data, _setData, removeData, ] = useTypedFastStorage<TrackRequest>(storageKey, initialResult?.data ?? null);
  const [email, _setEmail, removeEmail, ] = useTypedFastStorage<TrackEmail[]>(storageKey + 'email', initialResult?.email ?? null);
  const [visibleRequestBtn, setVisibleRequestBtn] = useState(initialResult?.visibleRequestBtn ?? false);

  useEffect(() => {
    if (initialResult) {
      if (!initialResult.groupVisible) {
        const element = document.getElementById('ifms-modal-width-element');
        if (element) {
          element.style.width = '350px';
        }
      }
    } else {
      initialize();
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose() {
    cleanup();
    onClose();
  }

  function cleanup() {
    removeData();
    removeEmail();
  }

  async function loadExisting() {
    const result: TrackRequest[] = await appContext.api.getTrackRequest(jobName, jobId, trType, mode === 'new');
    // if (result.length < 0) {
    //   await showGlobalOkDialog('Cannot load data');
    //   doClose();
    //   return;
    // }
    if (result[0]?.status !== 'tracking_stopped') setVisibleRequestBtn(true);

    if (result[0]?.id <= 0) {
      // NoticeTo 는 백엔드(GetTrackRequest)에서 같은 customer 의 직전 NoticeTo 를 자동채움해주므로 보존
      result[0].requestKind = trType === 'M' ? RequestType.bl : RequestType.Container;
      setNewRequest(true);
    }

    if (result[0]?.id > 0 && result[0].status === 'failed') {
      if (result[0].statusDesc === 'not_found' || result[0].statusDesc === 'awaiting_manifest' ||
        result[0].statusDesc.indexOf('is not a valid') > -1) {
        setNewRequest(true);
        setVisibleRequestBtn(true);
      } else if (result[0].statusDesc === 'retries_exhausted') {
        let trDate = new Date(result[0].u2Date);
        let eta = new Date(result[0].eta);

        if (trDate && eta && trDate < new Date(eta)) {
          setNewRequest(true);
          setVisibleRequestBtn(true);
        } else {
          setVisibleRequestBtn(false);
        }
      } else {
        setVisibleRequestBtn(false);
      }
    }

    return result;
  }

  //InitAccordion
  function InitTrackRequestGroup(trs: TrackRequest[]) {
    groupTitleRef.current = trs[0].requestKind === RequestType.bl ? 'By MBL' : 'By Container'

    trs.sort((a, b) => a.cntrNo.localeCompare(b.cntrNo)); //trs.OrderBy(x => x.CntrNo))
    trs.forEach(tr => {
      const divElement = document.createElement('div');
      const text = _.isEmpty(tr.cntrNo) ? 'NO CONTAINER#' : tr.cntrNo;
      divElement.innerHTML = (tr.status === 'failed' ? `<img src="${Tracking_R}"/> ` : '') + text;
      divElement.className = 'trackingrequest-item';
      divElement.dataset.tooltip = tr.status === 'failed' ? tr.statusDesc : '';
      groupItem.current.push(divElement);
    })
  }

  function checkPopupWidth(groupDataLength: number) {
    if (groupDataLength === 1 && jobName === JobNames.OIContainer) {
      groupVisibleRef.current = false;

      const element = document.getElementById('ifms-modal-width-element');
      if (element) {
        element.style.width = '350px';
      }
    }
  }

  async function initialize() {
    if (!data) {
      const groupData = await loadExisting();
      if (groupData) {
        InitTrackRequestGroup(groupData)
        checkPopupWidth(groupData.length);
        setData(groupData[0]);

        if(groupData[0].noticeTo) {
          let tempList: TrackEmail[] = [];
          const splitEmails = groupData[0].noticeTo.split(';');
          splitEmails.map(x => tempList.push({ email: x}));
          setEmail(tempList);
        } else {
          let tempList: TrackEmail[] = [];
          setEmail(tempList);
        }
      }
    }
    setInitialized(true);
  }

  if (!initialized || !data) {
    return null;
  }

  function setData(change: Partial<TrackRequest>) {
    _setData(change);
  }
  
  function setEmail(change: Partial<TrackEmail[]>) {
    _setEmail(change);
  }

  function deleteRow(index: number): boolean {
    debugger;
    if(!email) {
      return false;
    }

    const element = email[index];

    email?.splice(index, 1);
    setEmail(email);
    return true;
  }

  function onAddButtonClick(){
    if(!email) return;
    const { newData } = addLineItem();
    email?.push(newData);
    setEmail(email);
  }

  function addLineItem() {
    const newItem = new TrackEmail();

    return {
      setDataFunc: (list: any) => setEmail(list),
      curListData: email ?? [],
      newData: newItem
    };
  }

  function getIndexOfFirstInvalidCell(rowIndex: number): number | undefined {
    if(!email![rowIndex].email){
      return 1;
    }
    return undefined;
  }

  function applyToLineItem(rowIndex: number, change: Partial<TrackEmail>|null) {
    if (!change || !email) {
      return;
    }
    const newData = [...email];
    const checkData = email[rowIndex];
    if (change.email !== checkData.email) {
      _.merge(newData[rowIndex], { ...change, hasBeenChanged: true });
      setEmail(newData);
      //setHasChanges(true);
    }
  }

  return (
    <div className='report-popup'>
      <TrackingRequestInitializedImpl
        data={data}
        email={email}
        jobId={jobId}
        close={doClose}
        jobName={jobName}
        trStatus={trStatus}
        newRequest={newRequest}
        groupTitle={groupTitleRef.current}
        groupItem={groupItem.current}
        groupVisible={groupVisibleRef.current}
        mode={mode}
        vcResult={vcResult}
        containerNo={containerNo}
        trType={trType}
        visibleRequestBtn={visibleRequestBtn}
        deleteRow={deleteRow}
        addLineItem={addLineItem}
        applyToLineItem={applyToLineItem}
        getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
        onAddButtonClick={onAddButtonClick}
      />
    </div>
  );
}

function TrackingRequestInitializedImpl({
  data,
  email,
  jobId,
  close,
  jobName,
  trStatus,
  newRequest,
  groupTitle,
  groupItem,
  groupVisible,
  mode,
  vcResult,
  containerNo,
  trType,
  visibleRequestBtn,
  deleteRow,
  addLineItem,
  applyToLineItem,
  getIndexOfFirstInvalidCell,
  onAddButtonClick
}: {
  data: TrackRequest,
  email: TrackEmail[] | null
  jobId: number
  close: VoidFunction,
  jobName: JobNames
  trStatus: string,
  newRequest: boolean,
  groupTitle: string,
  groupItem: HTMLDivElement[],
  groupVisible: boolean,
  mode: string,
  vcResult: string,
  containerNo: string,
  trType: string,
  visibleRequestBtn: boolean,
  deleteRow: (rowIndex: number) => boolean | Promise<boolean>
  addLineItem: () => { setDataFunc: (data: any, focusRowIndex: number | undefined) => void; curListData: any; newData: any; } | undefined
  applyToLineItem: (rowIndex: number, change: any) => void
  getIndexOfFirstInvalidCell: ((rowIndex: number) => number | undefined) | undefined
  onAddButtonClick: () => void,
}) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const jobListContext = useContext(JobListViewContext);
  const fmsGlobal = getFmsGlobal();

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'trackRequest-email-list',
    tableName: 'emailList',
    ...trackEmailTable,
    height: `82px`,
    data: (email ?? []).map((x: any, i: number) => ({...x, applyToLineItem})),
    rowRenderers: [
      { accessor: 'email', EditCell: emailRenderer, width: 150 },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'email')
        return { type: TableCellTypes.Text, value: email![rowIndex].email };
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      return !visibleRequestBtn;
    },
    useEditControls: visibleRequestBtn,
    //useTableHelperText: true,
    tableHelperWhiteSpace: 'nowrap',
    isModalTable: true,
    disabledAddButton : !((visibleRequestBtn) && !(fmsGlobal.BlockTrackingService?? false))
  };

  function clickItem(e: any) {
    const alreadySettingsMenuOpend = document.querySelectorAll('.trackingrequest-item-selected');
    for (let i = 0; i < alreadySettingsMenuOpend.length; ++i) {
      alreadySettingsMenuOpend[i].className = `trackingrequest-item`;
    }

    e.currentTarget.className = 'trackingrequest-item-selected';
  }

  async function btnRequestCancel() {
    const apiResult: ApiResult<number> = await appContext.api.trackingCancelRequest(jobName, jobId);
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }

    jobListContext.reloadData();
    close();
  }

  async function btnSave() {
    let emailTo = data.noticeTo;

    const noticeToUpdates: {
      jobName: string,
      jobId: number,
      emailTo: string
    }[] = [];

    let emailListJoin = '';

    _.forEach(email, function(value) {
      if(value.email) {
        if(emailListJoin) emailListJoin += ';';
        emailListJoin += value.email;
      }
    });

    // console.log(emailListJoin);
    debugger;

    if (data.noticeTo !== emailListJoin || newRequest) {
      if (emailListJoin === '') {
        emailTo = emailListJoin;
      } else {
        emailTo = emailListJoin.trim()
          .replace(/\r?\n/g, "")
          .replace(/\s/g, "")
          .replace(/,/g, "");

        if (!validateEmails(emailTo)) {
          await showGlobalOkDialog(<p>Please check email list again!</p>);
          return;
        }
      }

      if (!newRequest) noticeToUpdates.push({ jobName: jobName, jobId: jobId, emailTo: emailTo });
    }

    if (noticeToUpdates.length > 0) {
      const apiResult: ApiResult<number> = await appContext.api.trackingUpdateRequest(noticeToUpdates);
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{"Save failure: " + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE)}</p>);
        close();
        return;
      }
    }
    else {
      let trackingRequestResult: {
        success: boolean,
        message: string
      } = { success: false, message: '' }

      if (jobName === JobNames.OIMbl || jobName === JobNames.OtherOP || jobName == JobNames.OEMbl)
        trackingRequestResult = await appContext.api.trackingRequestByMbl(jobName, jobId, vcResult, emailTo);
      else
        trackingRequestResult = await appContext.api.trackingRequestByContainer(containerNo, emailTo);

      if (!trackingRequestResult || !trackingRequestResult.success) {
        await showGlobalOkDialog(<p>{trackingRequestResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
        // close();
        return;
      }
    }
    jobListContext.reloadData(false);
    
    close();
  }

  return (
    <div id='trackrequestpopup'>
      <div style={{ display: 'flex' }}>

        <div id='trackrequestgroup' style={{ overflowY: 'scroll', overflowX: 'hidden', width: groupVisible ? '35%' : '0px', marginTop: '10px', height: '202px' }}>
          <div style={{ borderBottom: '1px solid', paddingLeft: '5px' }}>
            <Label style={{ color: 'gray' }} bold label={groupTitle} ></Label>
          </div>

          {groupItem.map((item, index) => (
            <div
              id={`trackrequestgroupitem-${index}`}
              onClick={clickItem}
              className={item.className}
              dangerouslySetInnerHTML={{ __html: item.innerHTML }}>
            </div>
          ))}
        </div>

        <div style={{ width: groupVisible ? '65%' : '100%' }}>
          <Fieldset noPadding noInlinePadding label={''}>
            <div style={{ padding: '5px' }}>
              <LabeledControl
                label='Master B/L #'
                labelSize='90px'
                bold
                control={
                  <TextBox
                    value={data.mblNo}
                    disabled
                  />
                }
              />
              <Label bold label={`Email Notice Recipients`} ></Label>
              <Label
                label={
                  <>
                    To add Email, press "Arrow Down" Key in any row. <br />
                    To delete Email, press "Delete" Key on selected row.
                  </>
                }
                style={{ fontSize: '10px', marginLeft: '12px' }}
              />
              <div style={{width: '261px'}}>
                <div style={{ paddingBottom: '3px', display: 'flex', justifyContent: 'flex-end' }}>
                  </div>
                <EditableTable
                  showTopBtn={true}
                  showTotalLineNum={false}
                  tableOptions={tableOptions}
                  deleteLine={deleteRow}
                  addLine={addLineItem}
                  getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
                  canDeleteRow={rowNumber => visibleRequestBtn}
                  hideAddBtn={false}
                  alignAddButtonRight={true}
                />
              </div>
              <InlineInputControlRow mode="w90" label="Created" >
                <LeftRightSplit
                  leftSize="calc(100% - 55px)"
                  rightSize="55px"
                  left={
                    <TextBox
                      value={mode === 'new' ? '' : formatDateTime(data.u1Date)}
                      disabled />
                  }
                  right={
                    <TextBox
                      value={mode === 'new' ? '' : data.u1Id}
                      disabled />
                  }
                />
              </InlineInputControlRow>

              <InlineInputControlRow mode="w90" label="Last modified" >
                <LeftRightSplit
                  leftSize="calc(100% - 55px)"
                  rightSize="55px"
                  left={
                    <TextBox
                      value={mode === 'new' ? '' : formatDateTime(data.u2Date)}
                      disabled />
                  }
                  right={
                    <TextBox
                      value={mode === 'new' ? '' : data.u2Id}
                      disabled />
                  }
                />
              </InlineInputControlRow>
            </div>
          </Fieldset>
        </div>

      </div>
      <div className='report-footer'>
        {(trStatus === 'Q' && !newRequest) && !(fmsGlobal.BlockTrackingService?? false) ?
          <SecondaryButton
            size="regular"
            style={{ width: '105px' }}
            onClick={btnRequestCancel}>
            Cancel Request
          </SecondaryButton>
          :
          <></>
        }
        {(visibleRequestBtn) && !(fmsGlobal.BlockTrackingService?? false)?
          <SecondaryButton
            size="regular"
            style={{ width: newRequest ? '70px' : '60px' }}
            onClick={btnSave}>
            {newRequest ? 'Request' : 'Update'}
          </SecondaryButton>
          :
          <></>
        }
        <SecondaryButton
          size="regular"
          style={{ width: '60px' }}
          onClick={close}>
          Close
        </SecondaryButton>

      </div>
    </div>
  );
}

const TrackingRequestPopup = ({
  jobId,
  storageKey,
  onClose,
  jobName,
  trStatus,
  trType,
  mode,
  vcResult,
  containerNo,
  sendTo,
  reloadData,
  prefetchedGroupData,
}: {
  jobId: number,
  storageKey: string,
  onClose: VoidFunction,
  jobName: JobNames,
  trStatus: string,
  trType: string,
  mode: string,
  vcResult: string,
  containerNo?: string,
  sendTo?:string,
  reloadData?: (trakingDate: string) => void,
  prefetchedGroupData?: TrackRequest[],
}) => {
  return (
    <TrackingRequestImpl
      jobId={jobId}
      jobName={jobName}
      key={storageKey}
      storageKey={storageKey}
      onClose={onClose}
      trStatus={trStatus}
      trType={trType}
      mode={mode}
      vcResult={vcResult}
      containerNo={containerNo ?? ''}
      sendTo={sendTo}
      reloadData={reloadData}
      prefetchedGroupData={prefetchedGroupData}
    />
  );
};

TrackingRequestPopup.defaultProps = {
};

TrackingRequestPopup.propTypes = {
  jobId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  jobName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrackingRequestPopup;
